import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, KeyRound, LogOut, Menu, Network, UserRound } from "lucide-react";
import { useAuth } from "../../../api/services/auth";
import { logout } from "../../../api/services/logoutUser";
import { userPermissions } from "../../../enums";
import { useNotification } from "../../../context/NotificationsContext";
import { Logo } from "../../brand/Logo";
import { Button } from "../../ui/button";
import { Tooltip } from "../../ui/tooltip";
import { cn } from "../../../lib/utils";
import Echo from "laravel-echo";
window.Pusher = require("pusher-js");

const Header = (props) => {
  const [notificationsList, setNotificationsList] = useState([]);
  const { notificationsCount, setNotificationsCount, options } =
    useNotification();
  const { permission, user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const location = useLocation();
  const userMenuRef = React.useRef(null);

  // Mobile menu toggle handler
  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
    document.body.classList.toggle("menuOpen", !menuOpen);
  };

  const userMessagesRef = React.useRef();

  useEffect(() => {
    if (notificationsList !== null) {
      userMessagesRef.current = notificationsList;
    }
  }, [notificationsList]);

  useEffect(() => {
    const echo = new Echo(options);

    echo?.private(`user.${user.id}`).listen("NotificationEvent", (data) => {
      if (
        !userMessagesRef.current?.some((msg) => msg.id === data.id) &&
        location.pathname !== "/notifications"
      ) {
        setNotificationsList([
          { created_at: data.time, content: data.message, id: data.id },
          ...userMessagesRef.current,
        ]);
      }
    });
  }, [location]);

  useEffect(() => {
    if (location.pathname === "/notifications") {
      localStorage.setItem("notificationCount", 0);
      setNotificationsCount(0);
      setNotificationsList([]);
    } else if (
      location.pathname !== "/notifications" &&
      notificationsList.length > 0
    ) {
      localStorage.setItem("notificationCount", notificationsList.length);
      setNotificationsCount(notificationsList.length);
    }
  }, [notificationsList.length]);

  // Close the avatar dropdown on outside click.
  useEffect(() => {
    if (!userMenuOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [userMenuOpen]);

  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent multiple clicks
    setIsLoggingOut(true);
    await logout();
  };

  const initials = user
    ? `${user.first_name?.charAt(0) ?? ""}${user.surname?.charAt(0) ?? ""}`.toUpperCase()
    : "PM";

  const roleLabel = user?.type
    ? user.type.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "";

  const notificationsActive = location.pathname === "/notifications";

  return (
    <header className="fixed inset-x-0 top-0 z-[100] flex h-14 items-center justify-between gap-4 border-b border-parch-100 bg-white px-4">
      <div className="flex min-w-0 items-center gap-4">
        <button
          type="button"
          aria-label="Toggle menu"
          className="grid h-8 w-8 place-items-center rounded-md text-ink-600 hover:bg-parch-50 lg:hidden"
          onClick={handleMenuToggle}
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="w-56 shrink-0 overflow-hidden">
          <Logo size="sm" />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <span className="badge badge-success hidden max-w-[10rem] truncate sm:inline-flex">
          {roleLabel}
        </span>
        {permission?.some((p) => p.name === userPermissions.CASES_LIST) && (
          <Tooltip label="Case Directory">
            <Link
              to="/cases-directory"
              aria-label="Case Directory"
              className={cn(
                "relative grid h-8 w-8 place-items-center rounded-full text-ink-600 transition hover:bg-parch-50 hover:text-ink-900",
                location.pathname === "/cases-directory" &&
                  "bg-parch-50 text-green-700"
              )}
            >
              <Network className="h-4 w-4" />
            </Link>
          </Tooltip>
        )}
        {permission?.some(
          (p) => p.name === userPermissions.NOTIFICATIONS_LIST
        ) && (
          <Link
            to="/notifications"
            aria-label="Notifications"
            className={cn(
              "relative grid h-8 w-8 place-items-center rounded-full text-ink-600 transition hover:bg-parch-50 hover:text-ink-900",
              notificationsActive && "bg-parch-50 text-green-700"
            )}
            onClick={() => {
              setNotificationsList([]);
              localStorage.setItem("notificationCount", 0);
            }}
          >
            <Bell className="h-4 w-4" />
            {!notificationsActive && notificationsCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 inline-flex min-w-4 items-center justify-center rounded-full bg-green-600 px-1 py-0.5 text-[10px] font-semibold leading-none text-white">
                {notificationsCount > 99 ? "99+" : notificationsCount}
              </span>
            )}
          </Link>
        )}
        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            aria-label="User menu"
            aria-expanded={userMenuOpen}
            aria-haspopup="menu"
            className="grid h-8 w-8 place-items-center overflow-hidden rounded-full bg-green-600 text-sm font-semibold text-white transition hover:ring-2 hover:ring-green-600/30 focus:outline-none focus:ring-2 focus:ring-green-600/40"
            onClick={() => setUserMenuOpen((value) => !value)}
          >
            {initials}
          </button>

          {userMenuOpen && (
            <div
              role="menu"
              className="absolute top-[calc(100%+0.5rem)] right-0 z-50 min-w-[12rem] overflow-hidden rounded-md border border-parch-100 bg-white py-1 shadow-lg"
            >
              <p className="truncate px-3 py-2 text-xs text-ink-400">
                {user.first_name} {user.surname}
              </p>
              {permission?.some(
                (p) => p.name === userPermissions.PROFILE_UPDATE
              ) && (
                <Link
                  to="/profile"
                  role="menuitem"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-ink-700 hover:bg-parch-50"
                  onClick={() => setUserMenuOpen(false)}
                >
                  <UserRound className="h-4 w-4" />
                  Profile
                </Link>
              )}
              <button
                type="button"
                role="menuitem"
                disabled={isLoggingOut}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink-700 hover:bg-parch-50 disabled:opacity-50"
                onClick={handleLogout}
              >
                <KeyRound className="h-4 w-4" />
                {isLoggingOut ? "Logging out..." : "Logout"}
              </button>
            </div>
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="hidden sm:inline-flex"
          disabled={isLoggingOut}
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" />
          {isLoggingOut ? "Logging out..." : "Logout"}
        </Button>
      </div>
    </header>
  );
};

export default Header;
